import type { NotificationOwnerDto } from '@verybigthings/trackframe-shared/schemas';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  formatScheduleDateTime,
  safeScheduleNotificationsForSchedule,
  summarizeScheduleNotifications,
} from './schedule-ui-model';

interface ScheduleNotificationsProps {
  notifications: readonly NotificationOwnerDto[];
  warning?: string | null;
}

interface ScheduleNotificationInlineProps {
  notifications: readonly NotificationOwnerDto[];
  scheduleId: string;
}

export function ScheduleNotifications({ notifications, warning }: ScheduleNotificationsProps) {
  const summary = summarizeScheduleNotifications(notifications);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Scheduler notifications</CardTitle>
            <CardDescription>
              Recent owner-scoped scheduler notices. Only kind, severity, read state, and redacted copy are shown.
            </CardDescription>
          </div>
          <Badge variant={summary.badgeVariant}>
            {summary.unreadCount} unread
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {warning ? (
          <div
            role="status"
            aria-live="polite"
            className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-700"
          >
            {warning}
          </div>
        ) : null}

        <p className="text-sm text-muted-foreground">Latest: {summary.latestLabel}</p>

        {notifications.length > 0 ? (
          <ul className="space-y-2" aria-label="Recent scheduler notifications">
            {notifications.slice(0, 5).map((notification) => (
              <li key={notification.id} className="rounded-md border px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={notification.severity === 'error' ? 'destructive' : notification.severity === 'warning' ? 'default' : 'secondary'}>
                    {notification.severity}
                  </Badge>
                  <span className="font-medium">{notification.title}</span>
                  <span className="text-xs text-muted-foreground">
                    {notification.kind} · {notification.readAt ? 'read' : 'unread'} · {formatScheduleDateTime(notification.createdAt)}
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">{notification.message}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No recent scheduler notifications.</p>
        )}
      </CardContent>
    </Card>
  );
}

export function ScheduleNotificationInline({ notifications, scheduleId }: ScheduleNotificationInlineProps) {
  const scoped = safeScheduleNotificationsForSchedule(notifications, scheduleId);
  const unreadCount = scoped.filter((notification) => notification.readAt === null).length;
  const latest = scoped[0];

  if (!latest) return <span className="text-muted-foreground">—</span>;

  return (
    <div className="max-w-64 space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        <Badge variant={latest.severity === 'error' ? 'destructive' : latest.severity === 'warning' ? 'default' : 'secondary'}>
          {latest.severity}
        </Badge>
        {unreadCount > 0 ? <Badge variant="outline">{unreadCount} unread</Badge> : null}
      </div>
      <p className="truncate text-xs" title={latest.title}>{latest.title}</p>
      <p className="text-xs text-muted-foreground">{latest.kind} · {formatScheduleDateTime(latest.createdAt)}</p>
    </div>
  );
}
